"use client";

import { useId } from "react";

/**
 * 読了・受け取り済みの印。革の本の表紙に金で押した、細い線の紋章（月桂樹の輪の中に開いた本、または星）。
 * 何の印か分かるよう、字（caption）があれば絵の代わりに輪の真ん中へ「既読」などを金の明朝で大きく入れる
 * （角判の朱印は「公務っぽい」、ひらがなの丸い札は「子供っぽい」、字のない紋章は「何の印か分からない」、帯の字は「崩れる」だったので、輪の真ん中に字にした）。
 * 金は下のナビの角金と同じ色。輪は下に暗い影・上に明るい縁を重ねて少し浮き出させ、字は崩れないよう暗い影だけにする。
 * 小さい印（一辺 36px 未満）は輪を描く余地がないので、真ん中の絵だけにする
 */

/** 月桂樹の片側の枝（左側。右は左右を返して使う）。輪の下から上へ、葉を交互に付ける */
function laurelLeaves(): { x: number; y: number; angle: number }[] {
  const leaves: { x: number; y: number; angle: number }[] = [];
  const r = 39;
  for (let i = 0; i < 7; i++) {
    // 下（100°）から上（235°）へ。角度は数学の向き（0°＝右、時計回りに増える画面の座標）
    const deg = 108 + i * 19;
    const a = (deg * Math.PI) / 180;
    const outward = i % 2 === 0 ? 1 : -1;
    leaves.push({
      x: 50 + (r + outward * 3.2) * Math.cos(a),
      y: 50 + (r + outward * 3.2) * Math.sin(a),
      // 枝の向き（接線）から外側／内側へ少し開く
      angle: deg + 90 + outward * 28,
    });
  }
  return leaves;
}
const LEAVES = laurelLeaves();

export default function LibrarySeal({
  text,
  size = 48,
  rotate = 0,
  stamp = false,
  emblem = "book",
  caption,
  label,
  className = "",
}: {
  /** 読み上げの名前（label が無いとき） */
  text: string;
  /** 一辺の長さ（px） */
  size?: number;
  /** 傾き（度） */
  rotate?: number;
  /** 押す動き（上から押されて少し沈む）を付ける */
  stamp?: boolean;
  /** 真ん中の絵。book＝開いた本、star＝星 */
  emblem?: "book" | "star";
  /** 輪の真ん中に入れる字（2〜4字。あれば絵の代わりに出す） */
  caption?: string;
  /** 読み上げの名前 */
  label?: string;
  className?: string;
}) {
  const id = useId().replace(/:/g, "");
  const withWreath = size >= 36;
  const withCaption = withWreath && !!caption;
  const gold = `url(#seal-gold-${id})`;
  const captionChars = caption ? [...caption].length : 0;
  // 輪の内側（幅およそ 50）に収まる大きさ。4字は2字ずつ2行
  const captionSize = captionChars <= 2 ? 25 : captionChars === 3 ? 18 : 17;
  const captionLines = caption && captionChars === 4 ? [[...caption].slice(0, 2).join(""), [...caption].slice(2).join("")] : caption ? [caption] : [];

  const art = (
    <>
      {withWreath && (
        <g>
          {/* 枝（左右）と、下で枝を結ぶ小さな結び目 */}
          <path d="M45 88 A39 39 0 0 1 22.5 21.5" fill="none" stroke={gold} strokeWidth="1.6" strokeLinecap="round" />
          <path d="M55 88 A39 39 0 0 0 77.5 21.5" fill="none" stroke={gold} strokeWidth="1.6" strokeLinecap="round" />
          <path d="M44 86 Q50 91 56 86" fill="none" stroke={gold} strokeWidth="2" strokeLinecap="round" />
          {LEAVES.map((l, i) => (
            <g key={i}>
              <ellipse cx={l.x} cy={l.y} rx="5.6" ry="2.3" transform={`rotate(${l.angle} ${l.x} ${l.y})`} fill={gold} />
              <ellipse cx={100 - l.x} cy={l.y} rx="5.6" ry="2.3" transform={`rotate(${180 - l.angle} ${100 - l.x} ${l.y})`} fill={gold} />
            </g>
          ))}
        </g>
      )}
      {!withCaption && <g transform={withWreath ? "translate(50 52) scale(0.55) translate(-50 -50)" : "translate(50 50) scale(1.05) translate(-50 -50)"}>
        {emblem === "book" ? (
          <>
            {/* 開いた本（左右の頁と真ん中の綴じ目・頁の線） */}
            <path d="M50 72 C41 64 29 63 18 66 V36 C29 33 41 34 50 42 C59 34 71 33 82 36 V66 C71 63 59 64 50 72 Z" fill="none" stroke={gold} strokeWidth="4" strokeLinejoin="round" />
            <path d="M50 42 V72" fill="none" stroke={gold} strokeWidth="3" />
            <path d="M26 45 C33 43.5 39 44 44 47 M26 54 C33 52.5 39 53 44 56 M74 45 C67 43.5 61 44 56 47 M74 54 C67 52.5 61 53 56 56" fill="none" stroke={gold} strokeWidth="2.2" strokeLinecap="round" />
          </>
        ) : (
          <path d="M50 14 L58.5 38.5 L84 39.5 L64 55 L71 80 L50 65.5 L29 80 L36 55 L16 39.5 L41.5 38.5 Z" fill={gold} />
        )}
      </g>}
    </>
  );

  return (
    <span
      role="img"
      aria-label={label ?? text}
      className={`inline-block shrink-0 ${stamp ? "library-seal-stamp" : ""} ${className}`}
      style={{ width: size, height: size, transform: rotate ? `rotate(${rotate}deg)` : undefined }}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} className="block overflow-visible" aria-hidden="true">
        <defs>
          {/* 下のナビの角金（#b8924f・#f3dca4）と同じ金 */}
          <linearGradient id={`seal-gold-${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f3dca4" />
            <stop offset=".45" stopColor="#d9b56f" />
            <stop offset="1" stopColor="#9c7638" />
          </linearGradient>
          {/* 字の影（暗い影だけ。光の縁を重ねると細い線が二重に見えて崩れる） */}
          <filter id={`seal-text-shadow-${id}`} x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0.6" dy="1" stdDeviation="0.5" floodColor="#000" floodOpacity=".75" />
          </filter>
          {/* 押し込んだ溝の影（右下）と縁の光（左上） */}
          <filter id={`seal-emboss-${id}`} x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0.8" dy="1.2" stdDeviation="0.6" floodColor="#000" floodOpacity=".7" />
            <feDropShadow dx="-0.5" dy="-0.5" stdDeviation="0.2" floodColor="#fff4d6" floodOpacity=".35" />
          </filter>
        </defs>
        <g filter={`url(#seal-emboss-${id})`}>{art}</g>
        {withCaption && (
          <text
            x="50"
            y="50"
            fill={gold}
            fontSize={captionSize}
            fontWeight={700}
            letterSpacing={captionChars <= 2 ? 1 : 0}
            textAnchor="middle"
            filter={`url(#seal-text-shadow-${id})`}
            style={{ fontFamily: "var(--font-mincho)" }}
          >
            {captionLines.map((line, i) => (
              <tspan key={i} x="50" y={50 + (i - (captionLines.length - 1) / 2) * (captionSize * 1.05)} dominantBaseline="central">
                {line}
              </tspan>
            ))}
          </text>
        )}
      </svg>
    </span>
  );
}
